export interface PollinatorGardenImageAsset {
  readonly key: string;
  readonly path: string;
  readonly width: number;
  readonly height: number;
  readonly kind: 'image';
  readonly runtimeReady: true;
}

const image = (
  key: string,
  path: string,
  width: number,
  height: number,
): PollinatorGardenImageAsset => ({
  key,
  path,
  width,
  height,
  kind: 'image',
  runtimeReady: true,
});

/** Approved production exports from pollinator-garden-production-assets.zip. */
export const POLLINATOR_GARDEN_ASSETS = {
  gardenDormant: image(
    'pollinator-garden-dormant',
    'assets/discovery-lab/pollinator-garden/backgrounds/pollinator-garden-dormant.png',
    980, 444,
  ),
  gardenRestored: image(
    'pollinator-garden-restored',
    'assets/discovery-lab/pollinator-garden/backgrounds/pollinator-garden-restored.png',
    988, 444,
  ),
  flowerPatchUnvisited: image(
    'pollinator-garden-flower-patch-unvisited',
    'assets/discovery-lab/pollinator-garden/interactives/flower-patch-unvisited.png',
    291, 312,
  ),
  flowerPatchVisited: image(
    'pollinator-garden-flower-patch-visited',
    'assets/discovery-lab/pollinator-garden/interactives/flower-patch-visited.png',
    288, 315,
  ),
  flowerPatchPollen: image(
    'pollinator-garden-flower-patch-pollen',
    'assets/discovery-lab/pollinator-garden/interactives/flower-patch-pollen.png',
    312, 315,
  ),
  flowerPatchSeedPods: image(
    'pollinator-garden-flower-patch-seed-pods',
    'assets/discovery-lab/pollinator-garden/interactives/flower-patch-seed-pods.png',
    408, 315,
  ),
  bee: image(
    'pollinator-garden-bee',
    'assets/discovery-lab/pollinator-garden/interactives/bee.png',
    240, 258,
  ),
  butterfly: image(
    'pollinator-garden-butterfly',
    'assets/discovery-lab/pollinator-garden/interactives/butterfly.png',
    249, 252,
  ),
  pollenEvidence: image(
    'pollinator-garden-pollen-evidence',
    'assets/discovery-lab/pollinator-garden/interactives/pollen-evidence.png',
    219, 264,
  ),
  visitEvidence: image(
    'pollinator-garden-visit-evidence',
    'assets/discovery-lab/pollinator-garden/interactives/visit-evidence.png',
    300, 264,
  ),
  seedEvidence: image(
    'pollinator-garden-seed-evidence',
    'assets/discovery-lab/pollinator-garden/interactives/seed-evidence.png',
    318, 264,
  ),
  missionPanel: image(
    'pollinator-garden-mission-panel',
    'assets/discovery-lab/pollinator-garden/ui/pollinator-mission-panel.png',
    402, 276,
  ),
  observePanel: image(
    'pollinator-garden-observe-panel',
    'assets/discovery-lab/pollinator-garden/ui/observe-panel.png',
    315, 276,
  ),
  evidencePanel: image(
    'pollinator-garden-evidence-panel',
    'assets/discovery-lab/pollinator-garden/ui/evidence-panel.png',
    342, 276,
  ),
  applyEvidenceButton: image(
    'pollinator-garden-apply-evidence-button',
    'assets/discovery-lab/pollinator-garden/ui/apply-evidence-button.png',
    318, 219,
  ),
  pollenTransferVfx: image(
    'pollinator-garden-pollen-transfer-vfx',
    'assets/discovery-lab/pollinator-garden/vfx/pollen-transfer-vfx.png',
    729, 333,
  ),
  restorationVfx: image(
    'pollinator-garden-restoration-vfx',
    'assets/discovery-lab/pollinator-garden/vfx/garden-restoration-vfx.png',
    696, 333,
  ),
} as const;

export const POLLINATOR_GARDEN_REQUIRED_ASSETS = Object.values(POLLINATOR_GARDEN_ASSETS);
export const POLLINATOR_GARDEN_PRODUCTION_READY = true;

export function preloadPollinatorGardenAssets(scene: Phaser.Scene) {
  POLLINATOR_GARDEN_REQUIRED_ASSETS.forEach(asset => scene.load.image(asset.key, asset.path));
}

export function getMissingPollinatorGardenAssets(
  source: Phaser.Scene | Phaser.Textures.TextureManager,
) {
  const textures = 'textures' in source ? source.textures : source;
  return POLLINATOR_GARDEN_REQUIRED_ASSETS.filter(asset => !textures.exists(asset.key));
}

export function isPollinatorGardenProductionReady(textures: Phaser.Textures.TextureManager) {
  return POLLINATOR_GARDEN_PRODUCTION_READY
    && getMissingPollinatorGardenAssets(textures).length === 0;
}
